import {
  AcademicCapIcon,
  ArrowsPointingOutIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ChartBarIcon,
  CheckBadgeIcon,
  ClockIcon,
  CubeIcon,
  DocumentIcon,
  DocumentTextIcon,
  IdentificationIcon,
  ScaleIcon,
  ShieldCheckIcon,
  SquaresPlusIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';
import type { SiteBrand } from '~/config/branding';

type LandingIcon = ComponentType<SVGProps<SVGSVGElement>>;

import type { LandingContent } from './landingContent';

export const HOME_CONTENT: Record<SiteBrand, LandingContent> = {
  mcv: {
    headline: ['Own Your Credentials', 'Control Your Career'],
    subheadline: 'A Free, Secure, AI-Powered Vault Built for Every Provider',
    description: 'Manage credentialing, compliance, and career insights – all in one place.',
  },
  hlx: {
    headline: ['Connecting People', 'Powering Care'],
    subheadline:
      'The AI-assisted healthcare labor exchange linking providers with the organizations that need them.',
    description:
      'Find your next opportunity or hire qualified, credential-ready providers – all in one place.',
  },
};

type Feature = { title: string; description: string; icon: LandingIcon };
type LandingDetails = {
  benefits: Feature[];
  categoriesTitle: string;
  categories: Feature[];
  joinTitle: string;
  joinDescription: string;
};

export const LANDING_DETAILS: Record<SiteBrand, LandingDetails> = {
  mcv: {
    benefits: [
      {
        title: 'Secure & Private',
        description: 'Your documents are encrypted and under your control.',
        icon: ShieldCheckIcon,
      },
      {
        title: 'AI-Assisted Automation',
        description: 'Extract information from uploaded documents and auto-populate your profile.',
        icon: DocumentTextIcon,
      },
      {
        title: 'Less Data Entry – More Control',
        description: 'Save time, review your information, and keep your profile current.',
        icon: ClockIcon,
      },
    ],
    categoriesTitle: 'Store, Organize, and Manage all of your Professional Documents',
    categories: [
      {
        title: 'Licenses',
        description: 'State, DEA, and\nmulti-state licensure',
        icon: IdentificationIcon,
      },
      {
        title: 'Certifications',
        description: 'Board certification,\nACLS, BLS, and more',
        icon: CheckBadgeIcon,
      },
      {
        title: 'Education',
        description: 'Medical school, residency,\nfellowship, and transcripts',
        icon: AcademicCapIcon,
      },
      {
        title: 'Employment',
        description: 'Work history, privileges,\nand credentials',
        icon: BriefcaseIcon,
      },
      { title: 'Insurance', description: 'Malpractice and\nliability coverage', icon: ScaleIcon },
      {
        title: 'Other Documents',
        description: 'CV, references,\nimmunizations, and more',
        icon: DocumentIcon,
      },
    ],
    joinTitle: 'Free for Healthcare Professionals',
    joinDescription: 'MedCredVault is currently provided at no cost.',
  },
  hlx: {
    benefits: [
      {
        title: 'AI-Powered Matching',
        description:
          'Analyze 50,000+ variables to identify relevant opportunities and clinical partners.',
        icon: UserGroupIcon,
      },
      {
        title: 'One Connected Marketplace',
        description: 'From individual shifts to enterprise clinical service agreements.',
        icon: SquaresPlusIcon,
      },
      {
        title: 'Smarter Workforce Decisions',
        description: 'Align qualifications, availability, and organizational requirements.',
        icon: ChartBarIcon,
      },
    ],
    categoriesTitle: 'One Exchange. Virtually Every Form of Healthcare Labor.',
    categories: [
      {
        title: 'Individual Shifts',
        description: 'A physician covering a weekend',
        icon: CalendarDaysIcon,
      },
      { title: 'Flexible Capacity', description: 'A clinician offering 0.2 FTE', icon: CubeIcon },
      {
        title: 'Locum Tenens',
        description: 'Temporary specialty coverage',
        icon: ArrowsPointingOutIcon,
      },
      {
        title: 'Clinical Teams',
        description: 'Ten nurses staffing a new unit',
        icon: UserGroupIcon,
      },
      {
        title: 'Physician Group Services',
        description: 'Overnight radiology coverage',
        icon: BuildingOffice2Icon,
      },
      {
        title: 'Enterprise Agreements',
        description: 'Multi-year anesthesia services',
        icon: BriefcaseIcon,
      },
    ],
    joinTitle: 'Join the Exchange',
    joinDescription: 'Be part of a smarter, more connected healthcare labor market.',
  },
};
